// A lab order carries only what the message carried: the department's code, not its
// name, and the ordering clinician's id, not their name. Both are joined here from
// data the page already holds, so the table and the details view never show a GP a
// raw code or a bare id.
//
// Everything degrades to something readable: an unmatched agency code is shown as
// itself (a laboratory the directory no longer publishes is still a real
// destination), and an unresolvable staff id is shown as nothing at all rather than
// as a number that means nothing at the point of care.

export const resolveAgencyName = (order, facilities) => {
  if (!order?.agencyCode) return "";

  const match = (facilities ?? []).find(
    (facility) =>
      facility.agencyCode === order.agencyCode &&
      String(facility.hospitalId) === String(order.hospitalId)
  );

  return match?.agencyName || order.agencyCode;
};

const resolveStaffName = (staffId, staffMembers) => {
  if (!staffId) return "";

  const match = (staffMembers ?? []).find(
    (staffMember) => String(staffMember.id) === String(staffId)
  );

  if (!match) return "";

  return [match.firstName, match.lastName].filter(Boolean).join(" ").trim();
};

// The GP the order went out under: whose Medical Council Number is on it.
export const resolveOrderedByName = (order, staffMembers) =>
  resolveStaffName(order?.staffId, staffMembers);

/*
 * Who actually placed it, when that is somebody else — a nurse ordering for a GP
 * (KIN-2100). Empty when the two are the same person, which is every order placed
 * before the two were recorded separately: `placedByStaffId` is null on those, and they
 * were always placed by the GP named on them.
 */
export const resolvePlacedByName = (order, staffMembers) => {
  if (!order?.placedByStaffId) return "";
  if (String(order.placedByStaffId) === String(order.staffId)) return "";

  return resolveStaffName(order.placedByStaffId, staffMembers);
};

// What the order was for (KIN-1977): the tests' names as the laboratory's directory
// labels them, in the order the GP added them. The backend sends names only — no
// clinical details — and sends null, not an empty list, until the completed order has
// been downloaded, so "not known yet" shows as nothing rather than as "no tests".
export const formatTestNames = (order) =>
  (order?.testNames ?? []).filter(Boolean).join(", ");
